import React, {
  type KeyboardEvent,
  type ReactElement,
  useEffect,
  useId,
  useRef,
  useState,
} from "react";
import ReactDOM from "react-dom";
import { LuMessageCircle } from "react-icons/lu";
import { toast } from "react-toastify";
import { useI18next } from "gatsby-plugin-react-i18next";

import ChatFallback from "@components/chat/chat_fallback";
import { CHAT_CHIP, CHAT_TOPICS } from "@components/chat/chat_helpers";
import ChatHome from "@components/chat/chat_home";
import ChatThread from "@components/chat/chat_thread";
import { useAuthenticationState } from "@stores/authentication";
import { selectUnread, useCustomerCareStore } from "@stores/customer_care";
import { useLiveChatState } from "@stores/live_chat";
import { cn } from "@utilities/helper";

type View =
  /** Home lists every chat (Chat history), so there is no All chats view. */
  | { name: "home" }
  | { name: "thread"; id: string }
  /** "Send a message": an empty conversation that is only created on the
   *  first bubble tap or typed message. */
  | { name: "draft" };

/**
 * Keeps something mounted for its exit animation (globals.css chat-*-out)
 * once `shown` turns false; `leaving` is true while it plays.
 */
const useLeaving = (
  shown: boolean,
  exitMs: number,
): { mounted: boolean; leaving: boolean } => {
  const [mounted, setMounted] = useState(shown);
  if (shown && !mounted) setMounted(true);
  useEffect(() => {
    if (shown || !mounted) return;
    const timer = window.setTimeout(() => {
      setMounted(false);
    }, exitMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [shown, mounted, exitMs]);
  return { mounted: shown || mounted, leaving: !shown && mounted };
};

/**
 * Support chat, ported from landing_page `feature/nextjs-redesign`
 * (components/chat; spec there: ai_docs/specs/customer-care-chat.md): the
 * floating button, the popular-topic chips it suggests on hover, and the
 * panel that replaces it in its corner while open (full screen on phones),
 * with the home (every chat as Chat history) and thread views. Data comes
 * from the Customer Care API through stores/customer_care; while its login
 * answers 404 the panel shows the legacy support-board iframe. Shown to
 * logged-in customers only, on every page. `stores/live_chat` keeps the open
 * flag so the help sections and the contacts page can open the panel.
 */
const ChatWidget = (): ReactElement | null => {
  const { t } = useI18next();
  const [isMounted, setMounted] = useState(false);
  const authenticationState = useAuthenticationState();
  const isAuthenticated = authenticationState.isAuthenticated();
  const liveChat = useLiveChatState();
  const open = liveChat.isOpenPopup;
  const bottomInset = Math.max(0, ...Object.values(liveChat.bottomInsets));
  const store = useCustomerCareStore();
  const unread = useCustomerCareStore(selectUnread);
  const [view, setView] = useState<View>({ name: "home" });
  const [peek, setPeek] = useState(false);
  const panelId = useId();
  const titleId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const peekTimer = useRef<number | undefined>(undefined);
  const peekBlocked = useRef(false);
  // The panel shrinks back into the corner (base · exit) and the topic chips
  // fade away (fast · exit) instead of vanishing. Once the panel has opened,
  // the chat button fades back in as it closes.
  const panel = useLeaving(open, 200);
  const chips = useLeaving(peek && !open && unread === 0, 150);
  const [wasOpened, setWasOpened] = useState(false);
  if (open && !wasOpened) setWasOpened(true);

  useEffect(() => {
    setMounted(true);
  }, []);

  // Connect once the customer is logged in (the badge needs the list); drop
  // everything on logout.
  useEffect(() => {
    if (isAuthenticated) {
      if (store.status === "idle") void store.connect();
    } else if (store.status !== "idle") {
      store.reset();
      liveChat.setIsOpenPopup(false);
    }
  }, [isAuthenticated, store.status]);

  // The list has no customer-side realtime: poll it, faster while open.
  useEffect(() => {
    if (store.status !== "ready") return;
    const timer = window.setInterval(
      () => {
        void store.refreshList();
      },
      open ? 30000 : 60000,
    );
    return () => {
      window.clearInterval(timer);
    };
  }, [store.status, open]);

  // The tab coming back into focus: the list may have moved meanwhile.
  useEffect(() => {
    if (store.status !== "ready") return;
    const onFocus = (): void => {
      if (document.visibilityState === "visible") void store.refreshList();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);
    return () => {
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, [store.status]);

  useEffect(() => {
    const panel = panelRef.current;
    if (open && panel !== null && !panel.contains(document.activeElement)) {
      panel.focus();
    }
  }, [open, view]);

  useEffect(
    () => () => {
      window.clearTimeout(peekTimer.current);
    },
    [],
  );

  const showPeek = (): void => {
    if (peekBlocked.current || unread > 0 || store.status !== "ready") return;
    window.clearTimeout(peekTimer.current);
    setPeek(true);
  };
  const hidePeek = (): void => {
    window.clearTimeout(peekTimer.current);
    peekBlocked.current = false;
    peekTimer.current = window.setTimeout(() => {
      setPeek(false);
    }, 150);
  };
  /** Where the home list was scrolled to, so back from a chat returns there. */
  const homeScroll = useRef(0);
  const openPanel = (): void => {
    window.clearTimeout(peekTimer.current);
    setPeek(false);
    homeScroll.current = 0;
    setView({ name: "home" });
    liveChat.setIsOpenPopup(true);
    void store.refreshList();
  };
  /** Leaving a thread for the home list: show the latest state of every chat. */
  const showHome = (): void => {
    setView({ name: "home" });
    void store.refreshList();
  };
  const closePanel = (): void => {
    window.clearTimeout(peekTimer.current);
    peekBlocked.current = true;
    setPeek(false);
    liveChat.setIsOpenPopup(false);
  };
  /** The close in the panel's header, or Escape (the chat button is hidden
   * while the chat is open): focus goes back to the button once it shows
   * again (the effect below). */
  const focusButtonOnClose = useRef(false);
  const closeFromPanel = (): void => {
    focusButtonOnClose.current = true;
    closePanel();
  };
  useEffect(() => {
    if (open || !focusButtonOnClose.current) return;
    focusButtonOnClose.current = false;
    buttonRef.current?.focus();
  }, [open]);

  /** A hover chip: creates the conversation with the topic as its first message. */
  // One conversation per tap: a second tap while the first is still being
  // created is ignored (it used to create a duplicate), and the thread view
  // opens as soon as the conversation exists so the tap has visible effect
  // before the topic message is sent.
  const startingRef = useRef(false);
  const [isStarting, setStarting] = useState(false);
  const startChat = async (topicKey: string): Promise<void> => {
    if (store.status !== "ready" || startingRef.current) return;
    startingRef.current = true;
    setStarting(true);
    const topic = t(topicKey);
    try {
      const id = await store.startConversation(topic);
      setView({ name: "thread", id });
      await store.send(id, topic);
    } catch (error) {
      console.error("[customer care] start conversation failed:", error);
      toast.error(t("try_again_later"), { autoClose: 4000 });
    } finally {
      startingRef.current = false;
      setStarting(false);
    }
  };

  const onKeyDown = (event: KeyboardEvent): void => {
    if (event.key !== "Escape") return;
    if (!open) {
      setPeek(false);
      return;
    }
    closeFromPanel();
  };

  if (!isMounted || !isAuthenticated) return null;

  const hover = {
    onMouseEnter: showPeek,
    onMouseLeave: hidePeek,
    onFocus: showPeek,
    onBlur: hidePeek,
  };

  const homeView = (): ReactElement => (
    <ChatHome
      titleId={titleId}
      conversations={store.conversations}
      onStart={topicKey => {
        if (topicKey !== undefined) void startChat(topicKey);
        else setView({ name: "draft" });
      }}
      onOpen={id => {
        setView({ name: "thread", id });
      }}
      onRefresh={async () => {
        await store.refreshList();
      }}
      scrollMemory={{
        get: () => homeScroll.current,
        set: value => {
          homeScroll.current = value;
        },
      }}
      onClose={closeFromPanel}
      isStarting={isStarting}
    />
  );

  const panelContent = (): ReactElement => {
    switch (store.status) {
      case "ready":
        if (view.name === "thread" || view.name === "draft") {
          return (
            <ChatThread
              key={view.name === "thread" ? view.id : "draft"}
              titleId={titleId}
              id={view.name === "thread" ? view.id : null}
              onCreated={id => {
                setView({ name: "thread", id });
              }}
              // No close here (Htet, 2026-10-07): back to home, then close.
              onBack={showHome}
            />
          );
        }
        return homeView();
      case "idle":
      case "connecting":
        // No spinner: the login runs in the background; the home view is
        // usable the moment the list arrives.
        return homeView();
      default:
        return (
          <ChatFallback
            status={store.status}
            onClose={closeFromPanel}
            onRetry={() => {
              store.reset();
              void store.connect();
            }}
          />
        );
    }
  };

  return ReactDOM.createPortal(
    <div
      onKeyDown={onKeyDown}
      className={cn(
        "pointer-events-none fixed inset-x-0 top-0 z-30 h-dvh w-screen",
        // Open: above the cookie banner (z-40); on phones the chat fills
        // the screen, navbar included.
        panel.mounted && "z-[45] max-md:z-[60]",
      )}
      // Something at the bottom of the screen (plan page order bar, checkout
      // Pay bar, cookie banner): the button, its chips and the panel sit
      // above it (hooks/useChatBottomInset).
      style={
        bottomInset > 0
          ? { height: `calc(100dvh - ${bottomInset}px)` }
          : undefined
      }
    >
      {panel.mounted && (
        <div
          ref={panelRef}
          id={panelId}
          role="dialog"
          aria-labelledby={titleId}
          tabIndex={-1}
          // Phones: full screen. Tablet and up: a 400px panel floating over
          // the page in the chat button's corner (the button hides while it
          // is open), 704px tall on tablets and 720px on desktop, its top
          // at least 144px from the screen top, clear of the header.
          className={cn(
            "fixed inset-0 flex flex-col overflow-clip bg-white pb-[env(safe-area-inset-bottom)] pt-[env(safe-area-inset-top)] outline-none md:absolute md:inset-auto md:bottom-6 md:end-6 md:h-[44rem] md:max-h-[calc(100%-10.5rem)] md:w-[calc(100%-2rem)] md:max-w-[25rem] md:origin-bottom-right md:rounded-3xl md:border md:border-gray-200 md:p-0 md:shadow-xl lg:bottom-10 lg:end-10 lg:h-[45rem] lg:max-h-[calc(100%-11.5rem)] rtl:origin-bottom-left",
            panel.leaving
              ? "chat-panel-out pointer-events-none"
              : "chat-panel-in pointer-events-auto",
          )}
        >
          {panelContent()}
        </div>
      )}

      {/* Hidden while the chat is open: the panel takes its corner and
          carries its own close in the header. 56px on phones, 60px on
          tablets (24px from the edges) and 64px on desktop (40px). */}
      <button
        ref={buttonRef}
        type="button"
        aria-label={
          unread > 0 ? t("chat_open_unread", { count: unread }) : t("open_chat")
        }
        aria-expanded={open}
        aria-controls={open ? panelId : undefined}
        onClick={openPanel}
        {...hover}
        className={cn(
          // bne-chat-toggle: the Egypt page tucks the button into the corner.
          "bne-chat-toggle pointer-events-auto absolute bottom-6 end-4 size-14 items-center justify-center overflow-clip rounded-full bg-gradient-to-br from-[#F97C50] to-[#FCBC65] text-white shadow-lg transition duration-150 hover:opacity-90 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-primary-200 active:scale-95 md:end-6 md:size-[60px] lg:bottom-10 lg:end-10 lg:size-16",
          open ? "hidden" : "flex",
          wasOpened && "chat-button-in",
        )}
      >
        <LuMessageCircle
          className="size-7 md:size-[30px] lg:size-8"
          aria-hidden="true"
        />
      </button>

      {unread > 0 && !open && (
        <span
          role="status"
          className="chat-chip-in pointer-events-none absolute bottom-16 end-3.5 flex h-6 min-w-6 items-center justify-center rounded-full border-2 border-white bg-black px-1 text-xs font-bold text-white md:bottom-[68px] md:end-[22px] lg:bottom-[88px] lg:end-[38px]"
        >
          <span aria-hidden="true">{unread > 99 ? "99+" : unread}</span>
          <span className="sr-only">{t("chat_unread", { count: unread })}</span>
        </span>
      )}

      {chips.mounted && !open && (
        <ul
          {...hover}
          aria-label={t("chat_popular_topics")}
          className={cn(
            "absolute bottom-24 end-4 m-0 flex list-none flex-col items-end gap-2 p-0 md:bottom-[100px] md:end-6 lg:bottom-[120px] lg:end-10",
            chips.leaving ? "pointer-events-none" : "pointer-events-auto",
          )}
        >
          {CHAT_TOPICS.map((topic, index) => (
            <li
              key={topic.icon}
              // In one after another from the button up; out together.
              className={cn(
                "m-0 p-0",
                chips.leaving ? "chat-chip-out" : "chat-chip-in",
              )}
              style={
                chips.leaving
                  ? undefined
                  : {
                      animationDelay: `${(CHAT_TOPICS.length - 1 - index) * 45}ms`,
                    }
              }
            >
              <button
                type="button"
                onClick={() => {
                  openPanel();
                  void startChat(topic.key);
                }}
                className={cn(CHAT_CHIP, "whitespace-nowrap")}
              >
                {t(topic.key)}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>,
    document.body,
  );
};

export default ChatWidget;
